import React, { useState, useEffect } from 'react';
import styles from './style.module.css';

const ShareIcon = () => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
        <polyline points="16 6 12 2 8 6" />
        <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
);

const PWAInstallPrompt = () => {
    const [showPrompt, setShowPrompt] = useState(false);
    const [showOpenAppPrompt, setShowOpenAppPrompt] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [installCheckInterval, setInstallCheckInterval] = useState(null);

    useEffect(() => {
        // Check if the app is already installed
        const isAppInstalled = window.matchMedia('(display-mode: standalone)').matches;
        const isIOSDevice = /iPhone|iPad|iPod/.test(navigator.userAgent);
        setIsIOS(isIOSDevice);

        if (!isAppInstalled) {
            window.addEventListener('beforeinstallprompt', (e) => {
                e.preventDefault();
                setDeferredPrompt(e);
                setShowPrompt(true);
            });

            if (isIOSDevice) {
                setShowPrompt(true);
            }

            window.addEventListener('appinstalled', (e) => {
                setShowPrompt(false);
                setShowOpenAppPrompt(true);
                if (installCheckInterval) {
                    clearInterval(installCheckInterval);
                }
            });
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', () => { });
            window.removeEventListener('appinstalled', () => { });
            if (installCheckInterval) {
                clearInterval(installCheckInterval);
            }
        };
    }, [installCheckInterval]);

    const checkInstallation = () => {
        // Check if app is in standalone mode
        const isAppInstalled = window.matchMedia('(display-mode: standalone)').matches;

        // Check installed related apps if available
        if (navigator.getInstalledRelatedApps) {
            navigator.getInstalledRelatedApps()
                .then(apps => {
                    const isInstalled = apps.some(app =>
                        app.url === window.location.origin
                    );

                    if (isInstalled || isAppInstalled) {
                        if (installCheckInterval) {
                            clearInterval(installCheckInterval);
                        }
                        setShowOpenAppPrompt(true);
                    }
                })
                .catch(err => console.error('Error checking installation:', err));
        } else if (isAppInstalled) {
            if (installCheckInterval) {
                clearInterval(installCheckInterval);
            }
            setShowOpenAppPrompt(true);
        }
    };

    const handleInstallClick = async () => {
        if (isIOS) {
            return;
        }

        console.log("🚀 ~ handleInstallClick ~ deferredPrompt:", deferredPrompt)
        if (deferredPrompt) {
            try {
                await deferredPrompt.prompt();
                const result = await deferredPrompt.userChoice;
                console.log("🚀 ~ handleInstallClick ~ result:", result)
                console.log("🚀 ~ handleInstallClick ~ result.outcome:", result?.outcome)
                if (result.outcome === 'accepted') {
                    setShowPrompt(false);

                    // Start checking if app is installed
                    const intervalId = setInterval(checkInstallation, 1000);
                    setInstallCheckInterval(intervalId);

                    // Safety timeout after 1 minute
                    setTimeout(() => {
                        if (installCheckInterval) {
                            clearInterval(installCheckInterval);
                        }
                    }, 60000);
                }
            } catch (error) {
                console.error('Error showing install prompt:', error);
            }
        }
    };

    const handleOpenApp = () => {
        // First inform the user

        // Try to close the browser window
        window.close();

        // If window.close() fails (which it might in some browsers),
        // at least hide our prompt
        setShowOpenAppPrompt(false);
    };

    if (!showPrompt && !showOpenAppPrompt) return null;

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                {showOpenAppPrompt ? (
                    <div className={styles.androidPrompt}>
                        <div className={styles.promptInfo}>
                            <div className={styles.header}>
                                <div>
                                    <h3>After the ReadMate App Installed,</h3>
                                    <p> we recommend closing your browser and opening ReadMate from your home screen for the best experience.</p>
                                </div>
                            </div>
                        </div>
                        <div className={styles.buttonGroup}>

                            <button
                                onClick={handleOpenApp}
                                className={styles.installButton}
                            >
                                X
                            </button>
                        </div>
                    </div>
                ) : isIOS ? (
                    <div className={styles.iosPrompt}>
                        <div className={styles.header}>
                            <ShareIcon />
                            <h3>Install ReadMate App</h3>
                        </div>
                        <div className={styles.instructions}>
                            <p>1. Tap the share button in your browser</p>
                            <p>2. Scroll down and tap "Add to Home Screen"</p>
                            <p>3. Tap "Add" to install</p>
                        </div>
                        <button
                            onClick={() => setShowPrompt(false)}
                            className={styles.dismissButton}
                        >
                            Dismiss
                        </button>
                    </div>
                ) : (
                    <div className={styles.androidPrompt}>
                        <div className={styles.promptInfo}>
                            <div className={styles.header}>
                                <div>
                                    <h3>Install ReadMate App</h3>
                                    <p>Install for a better experience</p>
                                </div>
                            </div>
                        </div>
                        <div className={styles.buttonGroup}>
                            <button
                                onClick={() => setShowPrompt(false)}
                                className={styles.laterButton}
                            >
                                Later
                            </button>
                            <button
                                onClick={handleInstallClick}
                                className={styles.installButton}
                            >
                                Install
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PWAInstallPrompt;